/**
 * Authentication hook
 * Manages authentication state and provides auth methods
 */

import { useState, useEffect, useCallback } from 'react';
import { UserProfile } from '../types/api';
import { authAPI, getToken } from '../utils/api';

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true
  });

  /**
   * Load user profile on mount if token exists
   */
  useEffect(() => {
    const loadUser = async () => {
      const token = getToken();
      if (!token) {
        setAuthState({ user: null, isAuthenticated: false, isLoading: false });
        return;
      }

      try {
        const profile = await authAPI.getProfile();
        setAuthState({
          user: profile,
          isAuthenticated: true,
          isLoading: false
        });
      } catch (error) {
        console.error('Failed to load user profile:', error);
        // Token might be invalid - clear it
        authAPI.signOut();
        setAuthState({ user: null, isAuthenticated: false, isLoading: false });
      }
    };

    loadUser();
  }, []);

  /**
   * Sign in with Google ID token
   */
  const signInWithGoogle = useCallback(async (idToken: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      const response = await authAPI.googleSignIn(idToken);
      setAuthState({
        user: response.user,
        isAuthenticated: true,
        isLoading: false
      });
      return response.user;
    } catch (error) {
      console.error('Sign in failed:', error);
      setAuthState({ user: null, isAuthenticated: false, isLoading: false });
      throw error;
    }
  }, []);

  /**
   * Sign out
   */
  const signOut = useCallback(() => {
    authAPI.signOut();
    setAuthState({ user: null, isAuthenticated: false, isLoading: false });
  }, []);

  return {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    signInWithGoogle,
    signOut
  };
}
